<template>
  <div class="BaseSelect">
    <label
      class="title"
      v-if="label"
      :for="id"
      >{{ label }}
    </label>
    <v-select
      :id="id"
      :aria-describedby="fieldValidationErrorMessage ? id + '-error' : undefined"
      :aria-invalid="!!fieldValidationErrorMessage"
      :aria-required="rules.length > 0"
      ref="$field"
      v-model="value"
      :items="items"
      :append-inner-icon="icon"
      item-title="label"
      item-value="value"
      density="compact"
      variant="outlined"
      :rules="rules"
      :hide-details="true"
      :disabled="disabled"
      :placeholder="t('common.select.placeholder')"
    />

    <ErrorMessageComponent
      :messages="fieldValidationErrorMessage"
      :id="id + '-error'"
    />
  </div>
</template>

<script setup lang="ts">
  import ErrorMessageComponent from '@/modules/core/components/formFields/ErrorMessageComponent.vue'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import type { OptionsValuesInterface, ValidationRuleType } from '@/modules/core/models/types'
  import { nextTick, onBeforeUnmount, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '../../composables/accesibilityComposable'

  const { t } = useI18n()

  interface PropsInterface {
    id: string
    modelValue: string | null
    items: OptionsValuesInterface[]
    rules?: ValidationRuleType[]
    label?: string
    icon?: string
    disabled?: boolean
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    items: () => [],
    rules: () => [],
    icon: 'icon-chevron-down'
  })

  const value = defineModel()

  const { $field, fieldValidationErrorMessage } = useValidationError()

  let observer: MutationObserver | null = null

  onMounted(async () => {
    // Esperar render inicial
    await nextTick()
    const rootElement = document.getElementById(props.id)?.parentElement
      ?.parentElement as HTMLElement | null
    observer = startDuplicatedLabelControl(rootElement)
  })

  onBeforeUnmount(() => {
    endDuplicatedLabelControl(observer)
  })
</script>

<style lang="scss" scoped>
  .BaseSelect {
    display: flex;
    flex-direction: column;
    gap: $gap-s;

    :deep(.v-select--active-menu) {
      .v-field__append-inner {
        i::before {
          rotate: 180deg;
        }
      }
    }

    label {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
    }

    :deep(.v-field) {
      border-radius: $b-radius-0;
      box-shadow: $box-shadow-0;
      transition: all 0.2s ease-in-out;

      /*     &:focus-within {
      box-shadow: $box-shadow-focus;
    } */

      input,
      .v-field__input {
        &:focus,
        &:focus-visible {
          outline: none;
          box-shadow: none;
        }
      }

      .v-field__append-inner {
        .mdi-menu-down {
          display: none;
        }

        i::before {
          font-family: $font-family-icons;
          color: $color-neutral-dark;
          transition: rotate 0.2s ease-in-out;
        }
      }
    }

    :deep(.v-input) {
      /* stylelint-disable-next-line no-descending-specificity */
      .v-field__input {
        min-height: 56px;
        padding: $spacing-l;
        color: $color-neutral-dark;
      }

      .v-field__outline__start,
      .v-field__outline__end {
        border-color: $color-neutral-light;
        opacity: 1;
      }

      &.v-input--disabled{
        background-color: $color-grey-300;

        .v-field__overlay{
          display:none;
        }
      }
    }
  }
</style>
